import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useParams, useOutletContext } from 'react-router-dom';
import BrandLoveCard from './BrandLoveCard';
import BrandLoveRanking from './BrandLoveRanking';
import DeiIndex from './DeiIndex';
import BrandBreakdown from './BrandBreakdown';
import PlatformBreakdown from './PlatformBreakdown';
import TopPosts from './TopPosts';
import { useTopicsData } from '../hooks/useTopicsData';
import { useBrandLoveData } from '../hooks/useBrandLoveData';
import { useDeiIndex } from '../hooks/useDeiIndex';
import CircularProgress from '@mui/material/CircularProgress';
import { useTopPostsData } from '../hooks/useTopPostsData';
import { useFilterContext } from '../context/FilterContext';

interface FilterProps {
  selectedBrands: string[];
  selectedPlatforms: string[];
  selectedPillars: string[];
  startDate: string | null;
  endDate: string | null;
}

const BrandTracker: React.FC = () => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  const { 
    startDate, 
    endDate, 
    selectedBrands, 
    selectedPlatforms,
    selectedPillars
  } = useOutletContext<FilterProps>();
  const { deiTargetPillar } = useFilterContext();

  // Split data fetching to prevent cross-contamination of dependencies
  const { data: otrosTopicsData, loading: otrosTopicsLoading, error: otrosTopicsError } = useTopicsData(
    orgId, 
    selectedPillars,
    startDate, 
    endDate
  );
  const brandLoveData = useBrandLoveData(
    orgId, 
    startDate, 
    endDate,
    selectedPlatforms,
    selectedPillars
  );
  // Hide DEI section only when explicitly set to 'All' (no specific DEI pillar).
  // Default to showing DEI if the field is missing (backwards compat with old filterset data).
  const showDei = deiTargetPillar !== 'All';
  const { data: deiIndexData, loading: deiIndexLoading, error: deiIndexError } = useDeiIndex(
    orgId,
    selectedPlatforms,
    selectedPillars,
    startDate,
    endDate,
    { enabled: showDei }
  );
  const { data: topPostsData, loading: topPostsLoading, error: topPostsError } = useTopPostsData(
    orgId,
    selectedBrands ?? [],
    selectedPillars ?? [],
    selectedPlatforms ?? [],
    startDate,
    endDate
  );

  // All useMemo hooks MUST be called unconditionally at the top level
  // Memoized brand love sections
  const renderBrandLove = useMemo(() => {
    // Show card even when loading (with loading overlay) if we have stale data
    if (!brandLoveData.data && !brandLoveData.loading) return null;

    return (
      <>
        <BrandLoveCard 
          data={brandLoveData.data}
          selectedBrands={selectedBrands}
          startDate={startDate}
          endDate={endDate}
          loading={brandLoveData.loading}
        />
        <BrandLoveRanking 
          data={brandLoveData.data}
          selectedBrands={selectedBrands}
          startDate={startDate}
          endDate={endDate}
        />
      </>
    );
  }, [
    brandLoveData.data, 
    selectedBrands,
    startDate, 
    endDate,
    brandLoveData.loading
  ]);

  // Memoized DEI Index section - hidden when deiTargetPillar is 'All' (no specific DEI pillar)
  const renderDeiIndex = useMemo(() => {
    if (!showDei) return null;
    // Show component even when loading (with loading overlay) if we have stale data
    if (!deiIndexData && !deiIndexLoading) return null;
    
    return (
      <DeiIndex 
        data={deiIndexData} 
        selectedBrands={selectedBrands}
        loading={deiIndexLoading}
      />
    );
  }, [showDei, deiIndexData, deiIndexLoading, selectedBrands]);

  // Memoized Topics data section - Use otherTopicsData for Brand/PlatformBreakdown
  const renderTopicsData = useMemo(() => {
    if (otrosTopicsLoading && !otrosTopicsData) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    if (!otrosTopicsData) return null;
    
    return (
      <>
        <BrandBreakdown 
          data={otrosTopicsData}
          selectedBrands={selectedBrands}
          selectedPlatforms={selectedPlatforms}
          selectedPillars={selectedPillars}
        />
        <PlatformBreakdown 
          data={otrosTopicsData}
          selectedBrands={selectedBrands}
          selectedPlatforms={selectedPlatforms}
        />
      </>
    );
  }, [
    otrosTopicsData, 
    otrosTopicsLoading, 
    selectedBrands, 
    selectedPlatforms, 
    selectedPillars
  ]);

  // Only show loading indicator for initial load when there's no data at all
  const isInitialLoading = useMemo(() => {
    return (otrosTopicsLoading && !otrosTopicsData) ||
           (brandLoveData.loading && !brandLoveData.data) ||
           (showDei && deiIndexLoading && !deiIndexData) ||
           (topPostsLoading && !topPostsData);
  }, [
    otrosTopicsLoading, otrosTopicsData,
    brandLoveData.loading, brandLoveData.data,
    showDei, deiIndexLoading, deiIndexData,
    topPostsLoading, topPostsData
  ]);

  // Memoize error handling to prevent unnecessary rerenders
  const errorMessage = useMemo(() => {
    return otrosTopicsError || brandLoveData.error || deiIndexError || topPostsError;
  }, [otrosTopicsError, brandLoveData.error, deiIndexError, topPostsError]);

  if (isInitialLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  if (errorMessage) {
    return (
      <Box sx={{ p: 3, color: 'error.main' }}>
        Error: {errorMessage}
      </Box>
    );
  }

  // Render the components with their memoized values
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {renderBrandLove}
      {renderDeiIndex}
      {renderTopicsData}
      {topPostsError ? (
        <Box sx={{ p: 3, color: 'error.main' }}>
          Error loading top posts: {topPostsError}
        </Box>
      ) : (
        <TopPosts 
          posts={topPostsData?.posts ?? null}
          total={topPostsData?.total ?? null}
          loading={topPostsLoading}
        />
      )}
    </Box>
  );
};

export default BrandTracker; 